import { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getPaymentRequests } from '../../../services/settlementService';

const PAYMENT_STATUS = {
  completed: { label: '결제완료', color: '#4A6CF7' },
  pending: { label: '대기', color: '#F59E0B' },
  failed: { label: '실패', color: '#EF4444' },
  confirming: { label: '확인중', color: '#3B82F6' },
  rejected: { label: '거절', color: '#EF4444' },
};

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const monthRange = (offset = 0) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + offset;
  return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
};

const PaymentHistory = () => {
  const { userData } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const thisMonth = monthRange(0);
  const [rangeMode, setRangeMode] = useState('this');
  const [fromDate, setFromDate] = useState(thisMonth.from);
  const [toDate, setToDate] = useState(thisMonth.to);

  useEffect(() => {
    if (!userData?.uid) return;
    setLoading(true);
    getPaymentRequests(userData.uid)
      .then((list) => {
        // paidAt 필드 추가 (completed 상태의 date 기반)
        const mapped = list.map((d) => ({
          ...d,
          paidAt: d.status === 'completed' ? d.date : '',
        }));
        setItems(mapped);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userData?.uid]);

  const applyRange = (mode) => {
    setRangeMode(mode);
    if (mode === 'prev') { const r = monthRange(-1); setFromDate(r.from); setToDate(r.to); }
    else if (mode === 'this') { const r = monthRange(0); setFromDate(r.from); setToDate(r.to); }
  };

  const filtered = useMemo(() => {
    return items.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (d.paidAt) {
        const dateStr = String(d.paidAt).slice(0, 10);
        if (fromDate && dateStr < fromDate) return false;
        if (toDate && dateStr > toDate) return false;
      }
      return true;
    });
  }, [items, statusFilter, fromDate, toDate]);

  const totalAmount = items.reduce((sum, d) => sum + (d.amount || 0), 0);
  const thisMonthPrefix = `${new Date().getFullYear()}-${pad2(new Date().getMonth() + 1)}`;
  const thisMonthAmount = items.filter((d) => d.status === 'completed' && String(d.paidAt || '').startsWith(thisMonthPrefix)).reduce((sum, d) => sum + (d.amount || 0), 0);
  const unpaidAmount = items.filter((d) => d.status === 'pending').reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <Container>
      <Title>결제 내역</Title>
      <SubText>DB 열람, 관리자 요청 등 모든 결제 기록입니다.</SubText>

      <SummaryScroll>
        <SummaryCard>
          <CardLabel>총 결제금액</CardLabel>
          <CardValue>{totalAmount.toLocaleString()}원</CardValue>
        </SummaryCard>
        <SummaryCard>
          <CardLabel>이번달 결제</CardLabel>
          <CardValue>{thisMonthAmount.toLocaleString()}원</CardValue>
        </SummaryCard>
        <SummaryCard>
          <CardLabel>미결제</CardLabel>
          <CardValue>{unpaidAmount.toLocaleString()}원</CardValue>
        </SummaryCard>
      </SummaryScroll>

      {/* 날짜 필터 */}
      <DateFilterRow>
        <ChipGroup>
          <Chip type="button" $active={rangeMode === 'prev'} onClick={() => applyRange('prev')}>전월</Chip>
          <Chip type="button" $active={rangeMode === 'this'} onClick={() => applyRange('this')}>당월</Chip>
          <Chip type="button" $active={rangeMode === 'custom'} onClick={() => { setRangeMode('custom'); const r = monthRange(0); setFromDate(r.from); setToDate(r.to); }}>직접선택</Chip>
        </ChipGroup>
        {rangeMode === 'custom' && (
          <DateRow>
            <DateInput type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <span>~</span>
            <DateInput type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </DateRow>
        )}
      </DateFilterRow>

      <FilterRow>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">전체</option>
          {Object.entries(PAYMENT_STATUS).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </Select>
        <CountText>{filtered.length}건</CountText>
      </FilterRow>

      {loading ? (
        <EmptyText>불러오는 중...</EmptyText>
      ) : filtered.length === 0 ? (
        <EmptyText>결제 내역이 없습니다.</EmptyText>
      ) : (
        <CardList>
          {filtered.map((d) => (
            <ItemCard key={d.id}>
              <CardTop>
                <CardId>{d.label || d.id}</CardId>
                <Badge $color={PAYMENT_STATUS[d.status]?.color || '#6B7280'}>{PAYMENT_STATUS[d.status]?.label || d.status}</Badge>
              </CardTop>
              <CardService>{d.memo || d.label || '-'}</CardService>
              <CardBottom>
                <CardAmount>{(d.amount || 0).toLocaleString()}원</CardAmount>
                <CardMeta>{d.method || '-'} / {d.paidAt || d.date || '-'}</CardMeta>
              </CardBottom>
            </ItemCard>
          ))}
        </CardList>
      )}
    </Container>
  );
};

export default PaymentHistory;

// --- Styled ---
const Container = styled.div`max-width:680px;@media(min-width:1024px){margin:0 auto;}`;
const Title = styled.h1`font-size:18px;font-weight:600;margin-bottom:4px;`;
const SubText = styled.p`font-size:13px;color:${({ theme }) => theme.colors.textSecondary};margin-bottom:20px;`;

const SummaryScroll = styled.div`
  display:flex;gap:12px;overflow-x:auto;padding-bottom:4px;margin-bottom:20px;
  -webkit-overflow-scrolling:touch;
  &::-webkit-scrollbar{display:none;}
  @media(min-width:${({ theme }) => theme.breakpoints.mobile}){display:grid;grid-template-columns:repeat(3,1fr);overflow:visible;}
`;
const SummaryCard = styled.div`min-width:160px;flex-shrink:0;background:${({ theme }) => theme.colors.bgWhite};padding:16px;border-radius:${({ theme }) => theme.radius.md};box-shadow:${({ theme }) => theme.shadow.md};
  @media(min-width:${({ theme }) => theme.breakpoints.mobile}){min-width:auto;}
`;
const CardLabel = styled.div`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};margin-bottom:6px;`;
const CardValue = styled.div`font-size:17px;font-weight:600;`;

const DateFilterRow = styled.div`
  margin-bottom: 16px;
`;

const ChipGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const Chip = styled.button`
  padding: 7px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active }) => $active ? '#4A6CF7' : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#6B7280'};
  border: 1px solid ${({ $active }) => $active ? '#4A6CF7' : '#E5E7EB'};
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DateInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  flex: 1;
`;

const FilterRow = styled.div`display:flex;gap:12px;align-items:center;margin-bottom:16px;`;
const Select = styled.select`padding:8px 12px;border:1px solid ${({ theme }) => theme.colors.border};border-radius:${({ theme }) => theme.radius.sm};font-size:13px;background:#fff;`;
const CountText = styled.span`margin-left:auto;font-size:13px;color:${({ theme }) => theme.colors.textSecondary};`;

const CardList = styled.div`display:flex;flex-direction:column;gap:12px;`;
const ItemCard = styled.div`background:${({ theme }) => theme.colors.bgWhite};border-radius:${({ theme }) => theme.radius.md};box-shadow:${({ theme }) => theme.shadow.sm};padding:16px;`;
const CardTop = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;`;
const CardId = styled.span`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};`;
const CardService = styled.div`font-size:14px;font-weight:500;margin-bottom:10px;`;
const CardBottom = styled.div`display:flex;justify-content:space-between;align-items:center;`;
const CardAmount = styled.span`font-size:15px;font-weight:700;`;
const CardMeta = styled.span`font-size:12px;color:${({ theme }) => theme.colors.textSecondary};`;
const Badge = styled.span`display:inline-block;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:600;color:${({ $color }) => $color || '#9CA3AF'};background:#fff;border:1px solid ${({ $color }) => $color || '#9CA3AF'};`;
const EmptyText = styled.div`padding:60px 0;text-align:center;color:${({ theme }) => theme.colors.textSecondary};font-size:13px;`;
